import videojs from 'video.js';

import ModalDialog from '../../components/modal-dialog.js';

import generateQuestionForm from './ask-a-question/question-form.js';
import { getMsFromString, enableFieldByCheckbox, padTimeBasedUponDuration, createValidationForTimeString } from '../../util/shared-control-bar.js';

import './ask-a-question.css';
import { sendQuestion } from '../../api/presentation.js';

import QuestionAnswerIcon from '../../assets/images/question_answer-white-24dp.svg';
import { isPresentationId } from '../../util';

const _modal = new WeakMap();
const _content = new WeakMap();
const _submitter = new WeakMap();
const _email = new WeakMap();
let localize;

/**
 * The AskAQuestion module.
 * @member {AskAQuestionPlugin} askAQuestion
 * @memberof MediasitePlayer#
 */

/**
 * Provides a modal dialog for the user to submit a question to the presenter, if enabled.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class AskAQuestionPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        const modal = new ModalDialog(player, {
            temporary: false,
            uncloseable: false,
            pauseOnOpen: false,
            description: localize('Ask a Question'),
            displayAsPanel: true
        });
        modal.addClass('mediasite-player__ask-a-question-modal');
        modal.addClass('mediasite-player__control-bar-modal');
        _modal.set(this, modal);
        player.controlbarMenu().addMenuItem(localize('Ask a Question'), { id: this.name, icon: QuestionAnswerIcon, callback: () => this.openModal() });

        player.ready(() => {
            player.layout().addChild(modal);
        });
    }

    /**
     * Opens the Ask a Question modal dialog.
     * @example
     * player.askAQuestion.openModal();
     */
    openModal() {
        if (isPresentationId(this.player.loader().presentationId) && this.enabled) {
            const modal = _modal.get(this);
            const content = generateQuestionForm(this.player);
            _content.set(this, content);

            this.setupModalContent(content);
            modal.fillWith(content);
            modal.open();
        }
    }

    /**
     * Closes the Ask a Question modal dialog.
     * @example
     * player.askAQuestion.closeModal();
     */
    closeModal() {
        _modal.get(this).close();
    }

    /**
     * @private
     * @param {HTMLDivElement} content
     */
    setupModalContent(content) {
        const form = content.querySelector('form');
        const nameField = content.querySelector('input[name=name]');
        const emailField = content.querySelector('input[name=email]');
        const timeCheckbox = content.querySelector('input[type=checkbox]');
        const timeField = content.querySelector('input[name=time]');

        //populate the name and email field with what was previously submitted, if applicatable
        nameField.value = _submitter.get(this) ?? '';
        emailField.value = _email.get(this) ?? '';

        enableFieldByCheckbox(timeField, timeCheckbox);
        timeField.value = padTimeBasedUponDuration(videojs.formatTime, this.player.currentTime(), this.player.duration());
        timeField.setAttribute("pattern", createValidationForTimeString(timeField.value));

        // When submitButton is clicked, validate and submit
        form.addEventListener('submit', event => {
            event.preventDefault();
            this.handleSubmit();
        });
    }

    handleSubmit() {
        /** @type {HTMLFormElement} */
        const formEl = _content.get(this).querySelector('form');
        const nameValue = formEl.querySelector('input[name=name]').value;
        const emailValue = formEl.querySelector('input[name=email]').value;

        //save the name and email for when the form loads again
        _submitter.set(this, nameValue);
        _email.set(this, emailValue);

        this.disableFormControls();
        const question = {
            Submitter: nameValue,
            SubmitterEmail: emailValue,
            Subject: formEl.querySelector('input[name=subject]').value,
            QuestionText: formEl.querySelector('textarea').value,
            TimeInPresentation: formEl.querySelector('input[type=checkbox]').checked ? getMsFromString(formEl.querySelector('input[name=time]').value) : 0
        };
        sendQuestion(this.player.reporting().playbackTicket, question)
            .then(result => this.handleSubmitComplete(result))
            .catch(error => this.handleSubmitError(error));
    }

    /**
     * @private
     * @param {object} submitResult
     * @param {boolean} submitResult.Success
     * @param {string} submitResult.Message
     */
    handleSubmitComplete(submitResult) {
        if (submitResult?.Success) {
            alert('Your question has been sent.');
            _modal.get(this).close();
        }
        else if (submitResult?.Message != null) {
            this.handleSubmitErrorMessage(submitResult.Message)
        }
        else {
            this.handleSubmitError()
        }
    }

    handleSubmitErrorMessage(message) {
        alert(`${message}`)
        this.enableFormControls();
    }

    handleSubmitError() {
        alert('Error!');
        this.enableFormControls();
    }

    enableFormControls() {
        const formEl = _content.get(this).querySelector('form');
        for (const el of Array.from(formEl.querySelectorAll('input:not([name=time]), textarea'))) {
            el.disabled = false;
        }

        const timeCheckbox = formEl.querySelector('input[type=checkbox]');
        const timeField = formEl.querySelector('input[name=time]');
        if (timeCheckbox.checked) {
            timeField.disabled = false;
        }
    }

    disableFormControls() {
        const formEl = _content.get(this).querySelector('form');
        for (const el of Array.from(formEl.querySelectorAll('input, textarea'))) {
            el.disabled = true;
        }
    }

    /**
     * A boolean indicating whether or not the Ask a Question feature is enabled.
     * @type {boolean}
     * @example
     * player.askAQuestion.enabled = true;
     */
    get enabled() {
        return this.player.controlbarMenu().isMenuItemEnabled(this.name);
    }

    set enabled(enabled) {
        if (enabled) {
            this.player.controlbarMenu().enableMenuItem(this.name);
        }
        else {
            this.player.controlbarMenu().disableMenuItem(this.name);
        }
    }

    dispose() {
        if (this.player?.usingPlugin('controlbarMenu')) {
            this.player.controlbarMenu().removeMenuItem(this.name);
        }
        
        super.dispose();
    }
}

export default AskAQuestionPlugin;
